// client/src/components/common/ErrorBoundary.tsx (TypeScript since ST-00216)
//
// ST-00215 / audit PLT-3. With React 19 an error thrown while rendering any page
// unmounts the whole root: the sidebar and tabs vanish and the user is left on a
// blank white page until they reload. Two boundaries now contain that:
//   - <RouteErrorBoundary> around the routed page (per tab pane when tabs are
//     on): the shell stays, the page shows "Something went wrong" with Try again
//     / Reload, and navigating to another page clears it;
//   - <ErrorBoundary fullScreen> around the whole app, for errors in the shell.
//
// Stale code after a deploy: hashed chunk files of the previous build are gone,
// so a tab that was open across a deploy fails to load a lazily imported chunk.
// That is not a bug in the page, it just needs the new build — the page reloads
// itself once (guarded, so a chunk that is genuinely missing cannot loop) and
// otherwise offers a Reload button. installChunkErrorReload() does the same for
// Vite's `vite:preloadError` event, which fires before React ever sees the error.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError/i;

const RELOAD_KEY = 'lb_chunk_reload_at';
// A second chunk failure this soon after the automatic reload means reloading
// does not help (e.g. the file is really missing): show the panel instead.
const RELOAD_GUARD_MS = 60 * 1000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown };
  if (e.name === 'ChunkLoadError') return true;
  return CHUNK_ERROR.test(String(e.message || error));
}

/**
 * Reload the page once to pick up the current build. Returns true when a reload
 * was started, false when it was suppressed (recent automatic reload, or no
 * sessionStorage to remember it in — never reload without the guard).
 */
export function reloadOnceForNewVersion(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (_) {
    return false;
  }
  window.location.reload();
  return true;
}

/** Vite dispatches `vite:preloadError` when a dynamic import or its deps fail. */
export function installChunkErrorReload(target: EventTarget = window): () => void {
  const onPreloadError = (event: Event) => {
    // preventDefault() stops Vite rethrowing; only do that when we reload.
    if (reloadOnceForNewVersion()) event.preventDefault();
  };
  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}

interface ErrorPanelProps {
  fullScreen: boolean;
  chunk: boolean;
  onRetry?: () => void;
}

function ErrorPanel({ fullScreen, chunk, onRetry }: ErrorPanelProps) {
  const title = chunk ? 'LexiBridge has been updated' : 'Something went wrong on this page';
  const body = chunk
    ? 'This page still had a piece of the previous version open. Reload to continue with the new one.'
    : fullScreen
      ? 'Reload to continue. If it keeps happening, let us know through Support in the menu.'
      : 'The rest of LexiBridge still works: pick another page from the menu, try again, or reload.';
  return (
    <div
      role="alert"
      className={
        fullScreen
          ? 'min-h-screen flex items-center justify-center p-6'
          : 'flex items-center justify-center px-4 py-10'
      }
      style={fullScreen ? { background: 'var(--surface-sunken, #f3f4f6)' } : undefined}
    >
      <div
        className="w-full max-w-md rounded-xl border p-6 text-center"
        style={{
          background: 'var(--surface, #ffffff)',
          borderColor: 'var(--border, #e5e7eb)',
          color: 'var(--text-strong, #111827)',
        }}
      >
        <h2 className="text-lg font-semibold mb-2">{title}</h2>
        <p className="text-sm mb-5" style={{ color: 'var(--text-muted, #6b7280)' }}>
          {body}
        </p>
        <div className="flex items-center justify-center gap-3">
          {!chunk && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: 'var(--border, #e5e7eb)' }}
            >
              Try again
            </button>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: 'var(--accent, #45b7aa)',
              color: 'var(--text-on-accent, #ffffff)',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}

export interface ErrorBoundaryProps {
  children?: ReactNode;
  /** The app-level boundary: a full-screen panel instead of an in-page one. */
  fullScreen?: boolean;
  /** A change (e.g. the pathname) clears a caught error: a new page gets a fresh start. */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  /**
   * Something was caught. Kept apart from `error`: JavaScript can throw anything, `null`, `0`
   * and '' included, and a falsy throw used to read as "no error", so the children rendered
   * again and threw again (ST-00230).
   */
  hasError: boolean;
  /** Whatever was thrown. */
  error: unknown;
  /** A stale-chunk reload was started; render nothing until the page goes. */
  reloading: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[ErrorBoundary] render error:', error, info && info.componentStack);
    if (isChunkLoadError(error) && reloadOnceForNewVersion()) {
      this.setState({ reloading: true });
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // A new page (resetKey = pathname) gets a fresh start.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset() {
    this.setState({ hasError: false, error: null, reloading: false });
  }

  override render() {
    const { hasError, error, reloading } = this.state;
    if (!hasError) return this.props.children;
    if (reloading) return null; // the page is about to reload
    return (
      <ErrorPanel
        fullScreen={!!this.props.fullScreen}
        chunk={isChunkLoadError(error)}
        onRetry={this.reset}
      />
    );
  }
}

/** Page-level boundary: must render inside a router; resets on navigation. */
export function RouteErrorBoundary({ children }: { children?: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}
